import React, { useEffect, useRef, useState } from 'react';
import { Modal, Field } from '../../components/ui';
import { INVITE_STATUSES, VISIBILITIES, normaliseText } from '../../utils/helpers';
import { MAX_FAMILY_SIZE } from './useMembers';

const ten = (v) => v.replace(/\D/g, '').slice(0, 10);

// Mobile (APK) guest form — built for adding MANY guests one after another on a
// phone: functions as tap chips (remembered from the last guest), Name + Mobile
// first with the right keyboards, "Save & add next" keeps the form open, and
// the rarely-used fields sit under "More details". All logic is in useMembers.
export default function MemberFormMobile({ vm }) {
  const {
    pickerFunctions, form, setF, setMobile, editing, fnName, me, selectedFunctionIds, toggleFunction,
    allFunctionsSelected, toggleAllFunctions, addedThisSession,
    party, addParty, setPartyField, removeParty, familyMode,
    addMode, setAddMode, distinctPeople, dupes, blockedSameFn,
    canImport, importContact, pickExisting, save, saving, setOpen,
    isMarriage, sideOptions, lockedSide,
  } = vm;
  const [more, setMore] = useState(!!editing);
  const nameRef = useRef(null);

  // After "Save & add next" the name clears — put the cursor back there.
  useEffect(() => { if (!editing && nameRef.current && form.GuestName === '' && form.MobileNumber === '') nameRef.current.focus(); }, [addedThisSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = editing ? 'Edit Guest' : (familyMode ? 'Add Family' : 'Add Guest');
  const canSave = !saving && !blockedSameFn && selectedFunctionIds.length > 0 && String(form.GuestName).trim() && /^\d{10}$/.test(String(form.MobileNumber));

  return (
    <Modal
      fullScreen
      title={addedThisSession ? `${title} · ${addedThisSession} added` : title}
      onClose={() => setOpen(false)}
      footer={editing ? (
        <>
          <button className="btn light" onClick={() => setOpen(false)}>Cancel</button>
          <button className="btn primary" style={{ flex: 1, padding: 12 }} disabled={saving || blockedSameFn} onClick={() => save()}>{saving ? 'Saving...' : 'Save'}</button>
        </>
      ) : (
        <>
          <button className="btn light" onClick={() => setOpen(false)}>Done</button>
          <button className="btn light" style={{ flex: 1, padding: 12 }} disabled={!canSave} onClick={() => save()}>
            {saving ? 'Saving...' : (familyMode ? 'Save family' : 'Save')}
          </button>
          <button className="btn primary" style={{ flex: 1.4, padding: 12 }} disabled={!canSave} onClick={() => save({ addNext: true })}>
            {saving ? 'Saving...' : 'Save & add next ➜'}
          </button>
        </>
      )}
    >
      {/* 1 · Functions — chips, remembered from the previous guest */}
      <div className="step-label">Invite to {selectedFunctionIds.length ? `(${selectedFunctionIds.length} selected)` : '— tap the functions'}</div>
      <div className="chips-row" style={{ marginBottom: 12 }}>
        {pickerFunctions.length === 0 && <span className="muted" style={{ fontSize: 12 }}>All functions of this event are over — nothing to invite to.</span>}
        {pickerFunctions.map(f => {
          const on = selectedFunctionIds.includes(String(f.FunctionId));
          return (
            <button key={f.FunctionId} type="button" disabled={f.past}
              className={'chip-toggle' + (on ? ' on' : '')} style={{ opacity: f.past ? 0.55 : 1 }}
              onClick={() => toggleFunction(f.FunctionId)}>
              {on ? '✓ ' : ''}{f.FunctionName}{f.past ? ' (over)' : ''}
            </button>
          );
        })}
        {pickerFunctions.filter(f => !f.past).length > 1 && (
          <button type="button" className="chip-toggle ghost" onClick={toggleAllFunctions}>{allFunctionsSelected ? 'None' : 'All'}</button>
        )}
      </div>

      {/* 2 · Who — source shortcuts, then Name + Mobile */}
      {!editing && !familyMode && (
        <div style={{ display: 'grid', gridTemplateColumns: canImport ? '1fr 1fr' : '1fr', gap: 8, marginBottom: 10 }}>
          {canImport && (
            <button type="button" className="btn light" style={{ padding: 11 }} onClick={importContact}>📇 Pick from phone contacts</button>
          )}
          <button type="button" className={`btn ${addMode === 'existing' ? 'primary' : 'light'}`} style={{ padding: 11 }}
            disabled={!distinctPeople.length} onClick={() => setAddMode(addMode === 'existing' ? 'new' : 'existing')}>
            {addMode === 'existing' ? '✕ Close list' : '↻ Already-added person'}
          </button>
        </div>
      )}
      {!editing && !familyMode && addMode === 'existing' && (
        <Field label="Re-invite an existing guest to more functions" type="select" value=""
          options={distinctPeople.map(p => ({ value: p.GuestId, label: `${[p.Surname, p.GuestName, p.FatherHusband].filter(Boolean).join(' ')} · ${p.MobileNumber || ''}` }))}
          onChange={pickExisting} />
      )}

      <label className="field">{familyMode ? 'Family head name' : 'Name'} *
        <input ref={nameRef} type="text" value={form.GuestName} placeholder="First name" autoComplete="off" enterKeyHint="next"
          style={{ fontSize: 16, padding: 12 }} onChange={(e) => setF('GuestName', normaliseText(e.target.value))} />
      </label>
      <label className="field">Surname
        <input type="text" value={form.Surname} placeholder="Surname" autoComplete="off" enterKeyHint="next"
          style={{ fontSize: 16, padding: 12 }} onChange={(e) => setF('Surname', normaliseText(e.target.value))} />
      </label>
      <label className="field">{familyMode ? 'Family head mobile' : 'Mobile number'} *
        <input type="tel" inputMode="numeric" maxLength={10} value={form.MobileNumber} placeholder="10-digit mobile" autoComplete="off" enterKeyHint="done"
          style={{ fontSize: 18, padding: 12, letterSpacing: 1 }} onChange={(e) => setMobile(e.target.value)} />
      </label>

      {!editing && dupes.length > 0 && (
        <div className="card card-pad" style={{ marginTop: 4, marginBottom: 10, background: blockedSameFn ? '#fee2e2' : '#fef3c7', fontSize: 13 }}>
          <strong>Already in this event:</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {dupes.map(d => (
              <li key={d.GuestId}>
                {(d.InvitedByName || '').trim() || 'Someone'} - {d.FunctionName || fnName(d.FunctionId)}{String(d.CreatedBy) === String(me) ? ' (you)' : ''}
              </li>
            ))}
          </ul>
          {blockedSameFn
            ? <div style={{ marginTop: 6, color: 'var(--danger)' }}>You already added this person to this function.</div>
            : <div style={{ marginTop: 6 }}>Another member invited them too — you can still add them to your list.</div>}
        </div>
      )}

      {/* Family members (family mode only) */}
      {!editing && familyMode && (
        <div style={{ marginTop: 6, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Family members</strong>
            <span className="muted" style={{ fontSize: 12 }}>{party.length + 1} incl. head</span>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn sm light" onClick={addParty} disabled={party.length >= MAX_FAMILY_SIZE - 1}>+ Member</button>
          </div>
          {party.map((m, i) => (
            <div key={i} className="card card-pad" style={{ padding: 10, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                <span className="muted" style={{ fontSize: 12 }}>{i + 2}.</span>
                <input placeholder="Name" value={m.GuestName} style={{ flex: 1, fontSize: 16, padding: 10 }}
                  onChange={e => setPartyField(i, 'GuestName', normaliseText(e.target.value))} />
                <button type="button" className="btn danger sm" onClick={() => removeParty(i)}>✕</button>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input placeholder="Surname" value={m.Surname} style={{ flex: 1, fontSize: 15, padding: 10 }}
                  onChange={e => setPartyField(i, 'Surname', normaliseText(e.target.value))} />
                <input placeholder="Mobile" type="tel" inputMode="numeric" maxLength={10} value={m.MobileNumber} style={{ flex: 1, fontSize: 15, padding: 10 }}
                  onChange={e => setPartyField(i, 'MobileNumber', ten(e.target.value))} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 3 · Side — no picker on mobile: a member is pinned to one side and an
           admin uses the dashboard Groom/Bride toggle. Shown read-only. */}
      {isMarriage && (
        <>
          <div className="step-label">Side</div>
          <div className="chips-row" style={{ marginBottom: 12 }}>
            <span className="chip-toggle on" aria-disabled="true">{lockedSide || form.Side}</span>
          </div>
        </>
      )}

      {/* 4 · Rarely used — collapsed */}
      <button type="button" className="linklike" style={{ fontSize: 13, marginBottom: 8 }} onClick={() => setMore(m => !m)}>
        {more ? '▾ Hide details' : '▸ More details (father/husband, visibility, remarks)'}
      </button>
      {more && (
        <div>
          {!familyMode && <Field label="Father / Husband name" value={form.FatherHusband} onChange={(v) => setF('FatherHusband', normaliseText(v))} />}
          <Field label="Visible to" type="select" value={form.Visibility}
            options={VISIBILITIES.map(v => ({ value: v, label: v === 'Private' ? 'Only me + admin (Private)' : 'Everyone (Common)' }))}
            onChange={(v) => setF('Visibility', v)} />
          {editing && (
            <>
              <Field label="Relation" value={form.Relation} onChange={(v) => setF('Relation', normaliseText(v))} />
              <Field label="Group" value={form.GroupName} onChange={(v) => setF('GroupName', normaliseText(v))} />
              <Field label="Invite Status" type="select" value={form.InviteStatus} options={INVITE_STATUSES} onChange={(v) => setF('InviteStatus', v)} />
              <Field label="Invited Via" type="select" value={form.InvitedVia} options={['WhatsApp', 'Call', 'Card']} onChange={(v) => setF('InvitedVia', v)} />
            </>
          )}
          <Field label="Remarks" type="textarea" rows={2} value={form.Remarks} onChange={(v) => setF('Remarks', v)} />
        </div>
      )}
    </Modal>
  );
}
